import React from 'react';
import ReactDOM from 'react-dom/client';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Auth0Provider } from '@auth0/auth0-react';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { LoggerProvider } from '@opengov/psc-react-logger';
import { getWebInstrumentations, UserActionInstrumentation } from '@grafana/faro-web-sdk';
import { TracingInstrumentation } from '@grafana/faro-web-tracing';
import theme from './theme';
import { App } from './App';
import { AUTH0_CALLBACK_PATH, auth0Config } from './utils';
import '@opengov/capital-mui-theme/dist/fonts.css';
import { LoggerContextProvider } from './services/logger/loggerContext';
import { queryClient } from './services/queryClient';
import { persister } from './services/queryPersistence';
import { detectPspHostingEnvironment, toggleDataCreatorVisibility } from './services/utils';
import { HostingEnvironment } from '@opengov/psp-contract';
import { buildTraceHeaderCorsPatterns } from './services/logger/traceHeaderAllowlist';
import { getTraceUserAttributes } from './services/logger/traceAttributes';
import { FaroUserMetaInstrumentation } from './services/logger/faroUser';
import { FaroSamplingRateInstrumentation } from './services/logger/faroSamplingRate';
import { recordSignIn } from './services/analytics/ogAnalytics';
import { restoreSignInReturnPath } from './services/auth/signInReturnPath';

const PUBLIC_SERVICE_PLATFORM_APP_FARO_COLLECTOR_URL =
  'https://faro-collector-prod-us-central-0.grafana.net/collect/bda94a05ee8b6d51db8a6528f8e80434';

const environment = detectPspHostingEnvironment();

// Cross-origin hosts allowed to receive W3C trace headers (traceparent/tracestate).
// Sourced from the validated, per-environment FQDN whitelist so we never propagate
// to a host that rejects the headers in CORS (which previously broke EAM — see
// services/logger/traceHeaderAllowlist.ts). Same-origin psp.<env> calls are always
// traced regardless of this list.
const propagateTraceHeaderCorsUrls = buildTraceHeaderCorsPatterns(environment);

// Register developer console helpers in non-production environments only.
// Usage: toggleDataCreatorVisibility() → toggles Data Creator sidebar visibility in integration.
if (
  environment === HostingEnvironment.DEVELOPMENT ||
  environment === HostingEnvironment.INTEGRATION
) {
  (window as unknown as { toggleDataCreatorVisibility?: () => void }).toggleDataCreatorVisibility =
    toggleDataCreatorVisibility;
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <PersistQueryClientProvider client={queryClient} persistOptions={{ persister }}>
      <Auth0Provider
        domain={auth0Config.domain}
        clientId={auth0Config.clientId}
        authorizationParams={{
          redirect_uri: auth0Config.redirectUri,
          audience: auth0Config.audience,
        }}
        cacheLocation="localstorage"
        // The shell only processes Auth0 redirect callbacks at its OWN redirect
        // path (/auth0/callback). A `?code&state` landing on any other route
        // belongs to a sub-app that runs its own Auth0 client (e.g. the PLC
        // employee module at /permitting-licensing), so the shell must NOT consume it —
        // otherwise it fails with "Invalid state" on the sub-app's transaction.
        // (CIT-19220 option-2 experiment.)
        skipRedirectCallback={window.location.pathname !== AUTH0_CALLBACK_PATH}
        // Auth0 calls this once per completed login, after the code exchange —
        // never for a restored session — so it is where `signed_in` is recorded,
        // and where the user is returned to the page the sign-in started from.
        onRedirectCallback={(appState) => {
          recordSignIn();
          restoreSignInReturnPath(appState);
        }}
      >
        <LoggerProvider
          config={{
            app: {
              name: 'public-service-platform-app',
              version: '1.0.0',
              environment,
            },
            loggerOutput: environment === 'development' ? 'console' : 'remote',
            url: PUBLIC_SERVICE_PLATFORM_APP_FARO_COLLECTOR_URL,
            // These instrumentations come from psp-web's @grafana/faro-* packages but are run by
            // the isolated Faro instance bundled inside @opengov/psc-react-logger, which never
            // sets psp-web's global `faro`. The user-action and performance instrumentations read
            // that global and throw: user actions during init from Faro 2.11, so the shell never
            // renders; performance from its PerformanceObserver.
            instrumentations: [
              // getWebInstrumentations, minus performance and user actions, collects Web Vitals
              // (LCP, FCP, INP, CLS, TTFB), navigation events, and JS error breadcrumbs
              ...getWebInstrumentations({ enablePerformanceInstrumentation: false }).filter(
                (i) => !(i instanceof UserActionInstrumentation)
              ),
              // TracingInstrumentation captures HTTP requests/responses including 4xx/5xx errors
              // Uses new OpenTelemetry semantic conventions (http.request.method, url.full, http.response.status_code)
              // propagateTraceHeaderCorsUrls enables traceparent injection into the
              // validated cross-origin hosts only; same-origin calls are always traced.
              new TracingInstrumentation({
                instrumentationOptions: {
                  propagateTraceHeaderCorsUrls,
                  // Tag every fetch/XHR span with the current user/entity. The
                  // attributes are sourced from a runtime store kept in sync by
                  // useTraceUserAttributes (identity isn't known at bootstrap).
                  // These callbacks are merged with Faro's defaults, not replaced.
                  fetchInstrumentationOptions: {
                    applyCustomAttributesOnSpan: (span) =>
                      span.setAttributes(getTraceUserAttributes()),
                  },
                  xhrInstrumentationOptions: {
                    applyCustomAttributesOnSpan: (span) =>
                      span.setAttributes(getTraceUserAttributes()),
                  },
                },
              }),
              // Captures the Faro `api` so useTraceUserAttributes can call setUser,
              // which populates the User association in the Faro dashboard.
              new FaroUserMetaInstrumentation(),
              // Records the sampling rate on every signal that has a session, so estimates stay
              // right if it changes. Its hook must run after the session instrumentation's. That
              // holds wherever this sits in the list: @opengov/psc-react-logger registers its own
              // session instrumentation ahead of this list, and Faro skips the one
              // getWebInstrumentations() returns as a duplicate. Relies on Faro's batching (on by
              // default): with it off, the first `session_start` is sent before this initializes,
              // without the rate.
              new FaroSamplingRateInstrumentation(),
            ],
          }}
        >
          <LoggerContextProvider>
            <ThemeProvider theme={theme}>
              <CssBaseline />
              <App />
            </ThemeProvider>
          </LoggerContextProvider>
        </LoggerProvider>
      </Auth0Provider>
      <ReactQueryDevtools initialIsOpen={false} />
    </PersistQueryClientProvider>
  </React.StrictMode>
);
